import { useMemo, useEffect } from 'react';
import { debounce } from 'lodash';
import { ChakraBox, ChakraFlex } from '@equip.health/ui';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { AppointmentStatusCounts } from '../constants';
import { STATUS_KEYS } from './statusUtils';
import { useStackedBarHover } from '~/lib/hooks/useStackedBarHover';
import MyDayStackedBar from './MyDayStackedBar';
import MyDayStatusLegendColumn from './MyDayStatusLegendColumn';

interface ProviderDashboardMyDayChartProps {
  appointments: AppointmentStatusCounts;
}

const ProviderDashboardMyDayChart = ({
  appointments,
}: ProviderDashboardMyDayChartProps) => {
  const { hoveredIndex, handleSegmentHover } = useStackedBarHover();
  const { trackEvent } = useAnalytics() ?? {};

  const trackSegmentHover = useMemo(
    () =>
      debounce((statusKey: string, count: number) => {
        trackEvent?.(ProviderDashboardEvents.myDayChartSegmentHovered, {
          statusKey,
          count,
          Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
        });
      }, 300),
    [trackEvent],
  );

  const trackLegendHover = useMemo(
    () =>
      debounce((statusKey: string, count: number) => {
        trackEvent?.(ProviderDashboardEvents.myDayLegendHovered, {
          statusKey,
          count,
          Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
        });
      }, 300),
    [trackEvent],
  );

  useEffect(() => {
    return () => {
      trackSegmentHover.cancel();
      trackLegendHover.cancel();
    };
  }, [trackSegmentHover, trackLegendHover]);

  return (
    <ChakraFlex alignItems="center" gap="28px" width="100%">
      <ChakraFlex direction="column" gap="4px" flexShrink={0} width="150px">
        <ChakraBox
          id="my-day-total"
          fontSize="24px"
          fontWeight="500"
          lineHeight="1"
          letterSpacing="-0.03em"
        >
          {appointments.total}
        </ChakraBox>
        <ChakraBox
          fontSize="12px"
          fontWeight="400"
          color="neutral.secondary"
          letterSpacing="-0.02em"
        >
          {appointments.total === 1
            ? 'appointment today'
            : 'appointments today'}
        </ChakraBox>
      </ChakraFlex>

      <ChakraFlex direction="column" gap="12px" flex="1" minWidth="0">
        <ChakraFlex gap="14px" flexWrap="nowrap" marginTop="4px">
          {STATUS_KEYS.map((key, index) => {
            const count = appointments[key];
            const shouldDim = hoveredIndex !== null && hoveredIndex !== index;
            return (
              <MyDayStatusLegendColumn
                key={key}
                statusKey={key}
                count={count}
                isHovered={!shouldDim}
                onMouseEnter={() => {
                  handleSegmentHover(index);
                  trackLegendHover(key, count);
                }}
                onMouseLeave={() => {
                  handleSegmentHover(null);
                  trackLegendHover.cancel();
                }}
              />
            );
          })}
        </ChakraFlex>

        <ChakraFlex marginTop="4px">
          <MyDayStackedBar
            appointments={appointments}
            onSegmentHover={(index) => {
              handleSegmentHover(index);
              if (index === null) {
                trackSegmentHover.cancel();
              }
            }}
            hoveredIndex={hoveredIndex}
            onTrackSegmentHover={trackSegmentHover}
          />
        </ChakraFlex>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default ProviderDashboardMyDayChart;
